import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronRight, Home } from 'lucide-react';
import { ROUTE_LABELS } from '@/lib/navConfig';
import { useCurrentBreadcrumbTitle } from '@/lib/breadcrumbStore';

const DETAIL_PARENTS = { material: '/search', listing: '/listings' };
const HIDDEN = new Set(['', 'login', 'register', 'legal', 'invitations', 'admin-invite', 'payment-success', 'marketplace-payment-success']);

const build = (pathname, t, dynamicTitle) => {
  const segs = pathname.split('/').filter(Boolean);
  if (segs.length === 0 || HIDDEN.has(segs[0])) return null;
  const crumbs = [{ label: t('breadcrumb.home'), to: '/' }];
  const [first, second, third] = segs;
  const labelKey = ROUTE_LABELS[first];
  if (!labelKey) return null;
  const parentTo = DETAIL_PARENTS[first] || `/${first}`;
  crumbs.push({ label: t(labelKey), to: parentTo });

  if (second === 'new') {
    crumbs.push({ label: t('breadcrumb.new') });
  } else if (second) {
    crumbs.push({ label: dynamicTitle || t('breadcrumb.details'), to: third ? `/${first}/${second}` : undefined });
    if (third === 'edit') crumbs.push({ label: t('breadcrumb.edit') });
  }
  return crumbs;
};

export const Breadcrumbs = () => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const dynamicTitle = useCurrentBreadcrumbTitle();
  const crumbs = build(pathname, t, dynamicTitle);
  if (!crumbs || crumbs.length < 2) return null;

  return (
    <nav aria-label="Breadcrumb" className="border-t border-border/40 bg-muted/30" data-testid="breadcrumbs">
      <ol className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center gap-1 h-8 text-xs text-muted-foreground overflow-x-auto whitespace-nowrap">
        {crumbs.map((c, i) => {
          const last = i === crumbs.length - 1;
          return (
            <li key={i} className="flex items-center gap-1 min-w-0">
              {i > 0 && <ChevronRight className="h-3 w-3 shrink-0 opacity-60" />}
              {last || !c.to ? (
                <span className="font-medium text-foreground truncate max-w-[260px]" aria-current="page" data-testid={`breadcrumb-${i}`}>
                  {i === 0 ? <Home className="h-3.5 w-3.5" /> : c.label}
                </span>
              ) : (
                <Link to={c.to} className="hover:text-orange-500 transition-colors truncate max-w-[200px]" data-testid={`breadcrumb-${i}`}>
                  {i === 0 ? <Home className="h-3.5 w-3.5" /> : c.label}
                </Link>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};
